/**
 * Automatic Free Gift Manager
 * Automatically adds/removes free gifts based on cart total thresholds
 * Optimized for speed and reliability
 */

(function() {
    window.AutoFreeGifts = window.AutoFreeGifts || new function() {
      var self = this;
      var isProcessing = false;
      var lastCartTotal = null;
      var operationQueue = [];
      var maxRetries = 3;
      
      // Threshold configuration (in cents)
      var THRESHOLDS = {
        pillowcase: 15000, // $150
        robe: 25000  // $250
      };
      
      // Cache for free gift variant IDs
      var freeGiftVariants = {
        pillowcase: null,
        robe: null
      };
      
      /**
       * Initialize the auto free gifts system
       */
      self.init = function() {
        console.log('[AutoFreeGifts] Initializing automatic free gift system');
        
        // Listen for cart updates - IMMEDIATELY check for free gifts
        document.addEventListener('cart:updated', function(e) {
          // Skip if this was an update from us
          if (e.detail && (e.detail.source === 'auto_free_gifts' || e.detail.source === 'side_cart_update_after_gift')) {
            console.log('[AutoFreeGifts] Skipping our own update');
            return;
          }
          
          // IMMEDIATELY check (no debounce for instant response)
          console.log('[AutoFreeGifts] Cart updated, checking free gifts immediately');
          self.checkAndUpdateFreeGifts(false);
        });
        
        // Initial check - wait for side cart to be available (optimized for speed)
        var initCheck = function(attempts) {
          attempts = attempts || 0;
          if (document.getElementById('side-cart') && attempts < 15) {
            self.extractVariantIds();
            if (freeGiftVariants.pillowcase && freeGiftVariants.robe) {
              console.log('[AutoFreeGifts] Initial check with variant IDs');
              self.checkAndUpdateFreeGifts(true);
            } else if (attempts < 15) {
              setTimeout(function() { initCheck(attempts + 1); }, 100);
            }
          } else if (attempts < 15) {
            setTimeout(function() { initCheck(attempts + 1); }, 100);
          }
        };
        setTimeout(initCheck, 200);
        
        // Periodic safety check every 5 seconds to ensure gifts are correct
        setInterval(function() {
          if (!isProcessing && freeGiftVariants.pillowcase && freeGiftVariants.robe) {
            console.log('[AutoFreeGifts] Periodic safety check');
            self.checkAndUpdateFreeGifts(false);
          }
        }, 5000);
      };
      
      /**
       * Extract variant IDs from free gift forms in the DOM
       * Called on every check to ensure we have the latest IDs
       */
      self.extractVariantIds = function() {
        var sideCart = document.getElementById('side-cart');
        if (!sideCart) {
          console.log('[AutoFreeGifts] Side cart not found, cannot extract variant IDs');
          return false;
        }
        
        var foundAll = true;
        var freeGiftForms = sideCart.querySelectorAll('.free-gift-form');
        
        if (freeGiftForms.length === 0) {
          console.log('[AutoFreeGifts] No free gift forms found in side cart');
          return false;
        }
        
        freeGiftForms.forEach(function(form) {
          var variantIdInput = form.querySelector('input[name="id"]');
          var freeGiftTypeInput = form.querySelector('input[name="properties[_free_gift]"]');
          
          if (variantIdInput && freeGiftTypeInput) {
            var giftType = freeGiftTypeInput.value;
            var variantId = variantIdInput.value;
            
            if (giftType && variantId) {
              if (!freeGiftVariants[giftType] || freeGiftVariants[giftType] !== variantId) {
                console.log('[AutoFreeGifts] Found/Updated variant for ' + giftType + ': ' + variantId);
              }
              freeGiftVariants[giftType] = variantId;
            } else {
              foundAll = false;
            }
          } else {
            foundAll = false;
          }
        });
        
        return foundAll;
      };
      
      /**
       * Get current cart state
       */
      self.getCartState = function() {
        return new Promise(function(resolve, reject) {
          fetch('/cart.js', {
            cache: 'no-cache'
          })
            .then(response => response.json())
            .then(cart => {
              var cartTotal = cart.total_price;
              var freeGiftsInCart = {
                pillowcase: null,
                robe: null
              };
              
              // Find free gifts already in cart
              cart.items.forEach(function(item) {
                if (item.properties && item.properties._free_gift) {
                  var giftType = item.properties._free_gift;
                  if (giftType === 'pillowcase' || giftType === 'robe') {
                    freeGiftsInCart[giftType] = item.key;
                  }
                }
              });
              
              resolve({
                total: cartTotal,
                giftsInCart: freeGiftsInCart,
                cart: cart
              });
            })
            .catch(error => {
              console.error('[AutoFreeGifts] Error fetching cart:', error);
              reject(error);
            });
        });
      };
      
    /**
     * Main function to check thresholds and update free gifts
     * Optimized for speed and reliability
     */
    self.checkAndUpdateFreeGifts = function(forceCheck) {
      if (isProcessing) {
        console.log('[AutoFreeGifts] Already processing, queuing check...');
        return Promise.resolve();
      }
      
      isProcessing = true;
      
      // Always re-extract variant IDs to ensure we have the latest
      self.extractVariantIds();
      
      // Validate we have the variant IDs we need
      if (!freeGiftVariants.pillowcase || !freeGiftVariants.robe) {
        console.log('[AutoFreeGifts] Missing variant IDs, skipping check');
        isProcessing = false;
        return Promise.resolve();
      }
      
      return self.getCartState().then(function(state) {
        console.log('[AutoFreeGifts] Cart total: $' + (state.total / 100).toFixed(2));
        
        // Check if cart total hasn't changed (avoid unnecessary processing)
        if (!forceCheck && lastCartTotal === state.total) {
          console.log('[AutoFreeGifts] Cart total unchanged, but verifying gifts are correct');
          // Even if total unchanged, verify gifts are in cart as expected
        }
        lastCartTotal = state.total;
        
        var giftsToAdd = [];
        var giftsToRemove = [];
        
        // Check pillowcase threshold
        if (state.total >= THRESHOLDS.pillowcase) {
          // Should have pillowcase
          if (!state.giftsInCart.pillowcase) {
            console.log('[AutoFreeGifts] Cart over $' + (THRESHOLDS.pillowcase/100) + ' but no pillowcase found');
            giftsToAdd.push('pillowcase');
          }
        } else {
          // Should NOT have pillowcase
          if (state.giftsInCart.pillowcase) {
            console.log('[AutoFreeGifts] Cart under $' + (THRESHOLDS.pillowcase/100) + ', removing pillowcase');
            giftsToRemove.push({type: 'pillowcase', key: state.giftsInCart.pillowcase});
          }
        }
        
        // Check robe threshold
        if (state.total >= THRESHOLDS.robe) {
          // Should have robe
          if (!state.giftsInCart.robe) {
            console.log('[AutoFreeGifts] Cart over $' + (THRESHOLDS.robe/100) + ' but no robe found');
            giftsToAdd.push('robe');
          }
        } else {
          // Should NOT have robe
          if (state.giftsInCart.robe) {
            console.log('[AutoFreeGifts] Cart under $' + (THRESHOLDS.robe/100) + ', removing robe');
            giftsToRemove.push({type: 'robe', key: state.giftsInCart.robe});
          }
        }
        
        // If no changes needed, exit early
        if (giftsToAdd.length === 0 && giftsToRemove.length === 0) {
          console.log('[AutoFreeGifts] No changes needed - gifts are correct');
          isProcessing = false;
          return Promise.resolve();
        }
        
        // Process removals and additions in PARALLEL for maximum speed
        var allOperations = [];
        
        // Queue all removals
        giftsToRemove.forEach(function(gift) {
          console.log('[AutoFreeGifts] Removing ' + gift.type);
          allOperations.push(self.removeGift(gift.key));
        });
        
        // Queue all additions
        giftsToAdd.forEach(function(giftType) {
          console.log('[AutoFreeGifts] Adding ' + giftType);
          allOperations.push(self.addGiftWithRetry(giftType, 0));
        });
        
        // Wait for ALL operations to complete in parallel (MUCH faster)
        return Promise.all(allOperations).then(function() {
          console.log('[AutoFreeGifts] All operations completed successfully');
          isProcessing = false;
          
          // Update the cart display immediately for speed
          if (window.SideCart && window.SideCart.updateSideCart) {
            window.SideCart.updateSideCart(window.SideCart.isOpen(), true);
          }
          
          // Optional: Quick verification in background (doesn't block UI)
          // The periodic check will catch any issues anyway
          if (giftsToAdd.length > 0 || giftsToRemove.length > 0) {
            setTimeout(function() {
              self.verifyCartState(giftsToAdd, giftsToRemove).then(function(verifyResult) {
                if (verifyResult.success) {
                  console.log('[AutoFreeGifts] Background verification passed');
                } else {
                  console.warn('[AutoFreeGifts] Background verification found issues (will auto-correct):', verifyResult.issues);
                }
              });
            }, 100);
          }
          
          return Promise.resolve();
        }).catch(function(error) {
          console.error('[AutoFreeGifts] Error updating gifts:', error);
          isProcessing = false;
          return Promise.reject(error);
        });
        
      }).catch(function(error) {
        console.error('[AutoFreeGifts] Error in checkAndUpdateFreeGifts:', error);
        isProcessing = false;
        return Promise.reject(error);
      });
    };
      
      /**
       * Add a free gift to the cart with retry mechanism
       */
      self.addGiftWithRetry = function(giftType, retryCount) {
        retryCount = retryCount || 0;
        
        return self.addGift(giftType).catch(function(error) {
          if (retryCount < maxRetries) {
            console.log('[AutoFreeGifts] Retrying add for ' + giftType + ' (attempt ' + (retryCount + 2) + '/' + (maxRetries + 1) + ')');
            // Quick retry for speed
            return new Promise(function(resolve) {
              setTimeout(function() {
                resolve(self.addGiftWithRetry(giftType, retryCount + 1));
              }, 100);
            });
          } else {
            console.error('[AutoFreeGifts] Failed to add ' + giftType + ' after ' + (maxRetries + 1) + ' attempts');
            throw error;
          }
        });
      };
      
      /**
       * Add a free gift to the cart
       */
      self.addGift = function(giftType) {
        return new Promise(function(resolve, reject) {
          var variantId = freeGiftVariants[giftType];
          
          if (!variantId) {
            console.warn('[AutoFreeGifts] No variant ID found for ' + giftType);
            resolve();
            return;
          }
          
          var data = {
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                '_free_gift': giftType,
                '_auto_added': 'true'
              }
            }]
          };
          
          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data)
          })
          .then(response => {
            if (!response.ok) {
              throw new Error('HTTP error! status: ' + response.status);
            }
            return response.json();
          })
          .then(data => {
            console.log('[AutoFreeGifts] Successfully added ' + giftType);
            resolve(data);
          })
          .catch(error => {
            console.error('[AutoFreeGifts] Error adding ' + giftType + ':', error);
            reject(error);
          });
        });
      };
      
      /**
       * Verify cart state after operations to ensure gifts were added/removed correctly
       */
      self.verifyCartState = function(giftsAdded, giftsRemoved) {
        return new Promise(function(resolve) {
          // Quick verification - minimal wait time for speed
          setTimeout(function() {
            self.getCartState().then(function(state) {
              var issues = [];
              var success = true;
              
              // Check if gifts that should be added are actually in cart
              giftsAdded.forEach(function(giftType) {
                if (!state.giftsInCart[giftType]) {
                  issues.push('Gift ' + giftType + ' was added but not found in cart');
                  success = false;
                  // Try to add it again
                  console.log('[AutoFreeGifts] Verification failed - re-adding ' + giftType);
                  self.addGiftWithRetry(giftType, 0);
                }
              });
              
              // Check if gifts that should be removed are actually gone
              giftsRemoved.forEach(function(gift) {
                if (state.giftsInCart[gift.type]) {
                  issues.push('Gift ' + gift.type + ' was removed but still in cart');
                  success = false;
                  // Try to remove it again
                  console.log('[AutoFreeGifts] Verification failed - re-removing ' + gift.type);
                  self.removeGift(state.giftsInCart[gift.type]);
                }
              });
              
              resolve({
                success: success,
                issues: issues
              });
            }).catch(function(error) {
              console.error('[AutoFreeGifts] Error verifying cart state:', error);
              resolve({
                success: false,
                issues: ['Failed to verify cart state']
              });
            });
          }, 150); // Reduced from 500ms to 150ms for speed
        });
      };
      
      /**
       * Remove a free gift from the cart
       */
      self.removeGift = function(lineItemKey) {
        return new Promise(function(resolve, reject) {
          if (!lineItemKey) {
            console.warn('[AutoFreeGifts] No line item key provided for removal');
            resolve();
            return;
          }
          
          var data = {
            updates: {}
          };
          data.updates[lineItemKey] = 0;
          
          fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data)
          })
          .then(response => {
            if (!response.ok) {
              throw new Error('HTTP error! status: ' + response.status);
            }
            return response.json();
          })
          .then(data => {
            console.log('[AutoFreeGifts] Successfully removed gift with key: ' + lineItemKey);
            resolve(data);
          })
          .catch(error => {
            console.error('[AutoFreeGifts] Error removing gift:', error);
            // Even if removal fails, resolve to continue with other operations
            resolve();
          });
        });
      };
    };
    
    // Auto-initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        // Wait for side cart to load
        setTimeout(function() {
          if (window.AutoFreeGifts) {
            window.AutoFreeGifts.init();
          }
        }, 1000);
      });
    } else {
      // DOM already loaded
      setTimeout(function() {
        if (window.AutoFreeGifts) {
          window.AutoFreeGifts.init();
        }
      }, 1000);
    }
  })();
  
  